import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import './QuestionBank.css'; 
import './SuperAdminQuestionBank.css';

const emptyForm = {
  question_text: '', topic_id: '', difficulty: 'medium',
  correct_answer: '', explanation: '',
  options: ['', '', '', ''],
  source: 'manual', tags: '',
  company_id: ''
};

const SuperAdminQuestionBank = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [metadata, setMetadata] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [filters, setFilters] = useState({ 
    search: '', 
    ownership: 'all', 
    college_id: '',
    company_id: '',
    page: 1,
    limit: 20
  });

  const [allColleges, setAllColleges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchTopics();
    fetchColleges();
    fetchCompanies();
    fetchQuestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchQuestions();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTopics = async () => {
    try {
      const res = await API.get('/questions/topics?includeEmpty=true');
      setTopics(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchColleges = async () => {
    try {
      const res = await API.get('/colleges');
      setAllColleges(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await API.get('/questions/companies');
      setCompanies(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await API.get('/questions', { params: { ...filters } });
      setQuestions(res.data.data);
      setMetadata(res.data.metadata);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await API.get(`/questions/${id}`);
      const q = res.data;
      const opts = (q.options || []).length > 0
        ? (typeof q.options[0] === 'string' ? q.options : q.options.map(o => o.option_text || o))
        : ['', '', '', ''];
      setForm({
        question_text: q.question_text,
        topic_id: q.topic_id || '',
        difficulty: q.difficulty || 'medium',
        correct_answer: q.correct_answer || opts[0] || '',
        explanation: q.explanation || '',
        options: opts,
        source: q.source || 'manual',
        tags: q.tags ? q.tags.filter(t => !t.startsWith('Company:')).join(', ') : '',
        company_id: q.company_id || ''
      });
      setEditingId(id);
      setShowForm(true);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!form.question_text.trim()) return setFormError('Question text is required');
    if (!form.topic_id) return setFormError('Please select a topic');
    if (form.options.some(o => !o.trim())) return setFormError('All options are required');
    if (!form.correct_answer) return setFormError('Please select correct answer');

    try {
      const tagsArray = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const payload = {
        ...form,
        tags: tagsArray,
        company_id: form.company_id || null
      };
      
      if (editingId) {
        await API.put(`/questions/${editingId}`, payload);
        setFormSuccess('Global Question updated!');
      } else {
        await API.post('/questions', payload);
        setFormSuccess('Global Question added!');
      }
      fetchQuestions();
      setTimeout(() => setShowForm(false), 1500);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question from global repository?')) return;
    try {
      await API.delete(`/questions/${id}`);
      fetchQuestions();
    } catch (err) { alert('Delete failed'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);

    try {
      const res = await API.post('/questions/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { message, summary, errors } = res.data;
      setBulkResults({ message, summary, errors: errors || [] });
      setShowBulkModal(true);
      fetchQuestions();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="sa-qb-container">
      <div className="sa-qb-header">
        <div>
          <h1>Global Repository Manager</h1>
          <p>Super Admin oversight of platform-wide intelligence</p>
        </div>
        <div className="sa-qb-actions">
           <button className="btn-bulk-sa" onClick={() => document.getElementById('bulkSaInput').click()}>
              <Icon name="plus" size={16} /> Bulk Upload
           </button>
           <input id="bulkSaInput" type="file" accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleBulkUpload}
           />
           <button className="btn-add-global" onClick={openAdd}>+ Add Global Question</button>
           <button className="btn-bulk-sa" onClick={() => navigate('/super-admin/topics')}>
             <Icon name="book" size={16} /> Manage Topics
           </button>
        </div>
      </div>

      <div className="sa-qb-filters">
        <div className="search-group">
          <span className="search-icon"><Icon name="search" size={16} /></span>
          <input 
            type="text" 
            placeholder="Search questions..." 
            value={filters.search}
            onChange={e => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
          />
          <select 
          className="sa-filter-select"
          value={filters.company_id}
          onChange={e => setFilters(prev => ({ ...prev, company_id: e.target.value, page: 1 }))}
        >
          <option value="">All Companies</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

        <select 
          className="sa-filter-select"
          value={filters.ownership}
          onChange={e => {
            const newOwnership = e.target.value;
            setFilters(prev => ({ 
              ...prev, 
              ownership: newOwnership, 
              college_id: newOwnership === 'global' ? '' : prev.college_id,
              page: 1 
            }));
          }}
        >
          <option value="global">Global Pool</option>
          <option value="college">College Specific</option>
          <option value="all">Everything</option>
        </select>

        {filters.ownership === 'college' && (
          <select
            className="sa-filter-select"
            value={filters.college_id}
            onChange={e => setFilters(prev => ({ ...prev, college_id: e.target.value, page: 1 }))}
          >
            <option value="">All Colleges</option>
            {allColleges.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="sa-qb-content">
        {loading ? (
          <div className="sa-loader">Analyzing database...</div>
        ) : (
          <div className="sa-table-wrapper">
            <table className="sa-questions-table">
              <thead>
                <tr>
                  <th>Question Summary</th>
                  <th>Topic</th>
                  <th>Diff</th>
                  <th>Added By</th>
                  <th>Origin</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map(q => (
                  <tr key={q.id}>
                    <td className="q-text-cell" title={q.question_text}>
                      {q.image_path && <span title="This question includes a chart" aria-label="Includes a chart"><Icon name="chart" size={13} /> </span>}
                      {q.question_text}
                    </td>
                    <td><span className="topic-tag">{q.topic_name}</span></td>
                    <td><span className={`diff-badge ${q.difficulty}`}>{q.difficulty}</span></td>
                    <td className="attribution-cell">
                      <div className="user-info">
                        <span className="user-name">{q.added_by_name}</span>
                        <span className="user-role">{q.created_by_role === 'super_admin' ? 'Super Admin' : 'College Admin'}</span>
                      </div>
                    </td>
                    <td>
                      {q.company_name ? (
                        <span className="origin-badge global" style={{ background: 'var(--z-tint-cyan)', color: '#0891b2' }}>
                          <Icon name="placement" size={13} /> {q.company_name}
                        </span>
                      ) : (
                        <span className={`origin-badge ${q.college_id ? 'college' : 'global'}`}>
                          {q.college_id ? 'College Specific' : 'Global Pool'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="icon-btn-edit" onClick={() => openEdit(q.id)} aria-label="Edit question"><Icon name="edit" size={16} /></button>
                        <button className="icon-btn-del" onClick={() => handleDelete(q.id)} aria-label="Delete question"><Icon name="close" size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {metadata.total_pages > 1 && (
              <div className="sa-pagination">
                <button disabled={filters.page <= 1} onClick={() => handlePageChange(filters.page - 1)}>Prev</button>
                <div className="page-numbers">Page {filters.page} of {metadata.total_pages}</div>
                <button disabled={filters.page >= metadata.total_pages} onClick={() => handlePageChange(filters.page + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <div className="sa-modal-overlay">
          <div className="sa-modal">
            <div className="sa-modal-header">
              <h2>{editingId ? 'Edit Global Question' : 'Add New Global Question'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            
            {formError   && <div className="alert error">{formError}</div>}
            {formSuccess && <div className="alert success">{formSuccess}</div>}

            <form onSubmit={handleSubmit}>
              <div className="sa-form-grid">
                <div className="sa-form-group full">
                  <label>Question Text *</label>
                  <textarea rows={3} required
                    value={form.question_text} 
                    onChange={e => setForm({...form, question_text: e.target.value})} />
                </div>
                
                <div className="sa-form-group">
                  <label>Topic *</label>
                  <select required value={form.topic_id}
                    onChange={e => setForm({...form, topic_id: e.target.value})}>
                    <option value="">Select Topic</option>
                    {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                <div className="sa-form-group">
                  <label>Difficulty</label>
                  <select value={form.difficulty}
                    onChange={e => setForm({...form, difficulty: e.target.value})}>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="very_hard">Very Hard</option>
                  </select>
                </div>

                <div className="sa-form-group full">
                  <label>Options (Select correct one)</label>
                  {form.options.map((opt, idx) => (
                    <div className="sa-opt-row" key={idx}>
                      <input type="radio" name="correct" 
                        checked={form.correct_answer === opt && opt !== ''}
                        onChange={() => setForm({...form, correct_answer: opt})} />
                      <input type="text" placeholder={`Option ${idx+1}`} required
                        value={opt} onChange={e => {
                          const newOpts = [...form.options];
                          newOpts[idx] = e.target.value;
                          setForm({...form, options: newOpts});
                        }} />
                    </div>
                  ))}
                </div>

                <div className="sa-form-group full">
                  <label>Explanation</label>
                  <textarea rows={2} value={form.explanation}
                    onChange={e => setForm({...form, explanation: e.target.value})} />
                </div>
                
                <div className="sa-form-group">
                   <label>Tags (Comma separated)</label>
                   <input type="text" value={form.tags}
                     onChange={e => setForm({...form, tags: e.target.value})} />
                </div>

                <div className="sa-form-group">
                  <label>Target Company (Optional)</label>
                  <select value={form.company_id}
                    onChange={e => setForm({...form, company_id: e.target.value})}>
                    <option value="">No Specific Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="sa-btn-save">Save Question</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && bulkResults && (
        <div className="sa-modal-overlay">
          <div className="sa-modal sa-bulk-modal">
            <div className="sa-modal-header">
              <h2>Bulk Import Summary</h2>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            
            <div className="bulk-stats-grid">
               <div className="b-stat"><span>Added</span><strong>{bulkResults.summary.added}</strong></div>
               <div className="b-stat"><span>Skipped</span><strong>{bulkResults.summary.skipped}</strong></div>
               <div className="b-stat"><span>Total</span><strong>{bulkResults.summary.total}</strong></div>
            </div>

            {bulkResults.errors.length > 0 && (
              <div className="sa-bulk-errors">
                <h4>Validation Errors</h4>
                <div className="error-log">
                   {bulkResults.errors.map((err, i) => (
                     <div key={i} className="err-row">
                       <span className="row-num">Row {err.row}:</span>
                       <span className="err-msg">{err.error}</span>
                     </div>
                   ))}
                </div>
              </div>
            )}
            
            <div className="sa-modal-footer">
               <button className="sa-btn-save" onClick={() => setShowBulkModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminQuestionBank;
